<template>
  <div class="cesium-container">
    <div ref="cesiumContainer" :class="['cesium-viewer', { 'crosshair-cursor': isCoordinatePickerActive }]"></div>
    <app-header id="header" :logout="logout"></app-header>
    <measure-tool-bar :showJakartoPointclouds=showJakartoPointclouds id="tool_bar" :viewer="viewer" :jakartoPointclouds="jakartoPointclouds"></measure-tool-bar>
    <div class="cesium-controls">
      <div class="basemap-picker">
        <select
          v-model="selectedBasemap"
          @change="changeBasemap"
          class="basemap-select"
        >
          <option value="none">No basemap</option>
          <option value="positron">Positron</option>
          <option value="darkmatter">Dark Matter</option>
          <option value="voyager">Voyager</option>
          <option value="satellite">Satellite</option>
        </select>
      </div>
      <button @click="toggleGoogle3DTiles" :class="['control-button', { 'control-button-activated': showGoogle3DTiles }]">
        {{
          showGoogle3DTiles ? "Hide Google 3D Tiles" : "Show Google 3D Tiles"
        }}
      </button>
      <button
        @click="toggleJakartoPointclouds" 
        :class="['control-button', { 'control-button-activated': showJakartoPointclouds }]">
        {{
          showJakartoPointclouds
            ? "Hide Jakarto Pointclouds"
            : "Show Jakarto Pointclouds"
        }}
      </button>
      <button
        @click="toggleAerialPointclouds" 
        :class="['control-button', { 'control-button-activated': showAerialPointclouds }]">
        {{
          showAerialPointclouds
            ? "Hide Aerial Pointclouds"
            : "Show Aerial Pointclouds"
        }}
      </button>
      <div v-if="showJakartoPointclouds || showAerialPointclouds" class="point-size-controls">
        <label>
          Point size: {{ pointSize }}
          <input
            type="range"
            min="1"
            max="4"
            step="1"
            v-model.number="pointSize"
            class="point-size-slider"
          />
        </label>
      </div>
      <div class="wmts-picker">
        <select
          v-model="selectedWMTS"
          @change="changeJakartoWMTS"
          class="basemap-select"
        >
          <option value="none">No WMTS</option>
          <option value="color">Color</option>
          <option value="color_lowest_z">Color of ground points</option>
          <option value="max_intensity">Max intensity</option>
          <option value="low_z_max_intensity">Max intensity of ground points</option>
          <option value="classification">Classification</option>
          <option value="hillshade">Hillshade</option>
          <option value="verticality">Verticality</option>
        </select>
      </div>
      <hr class="separator" />

      <div class="view-controls">
        <button @click="saveView" class="control-button">Save View</button>
        <button
          @click="loadView"
          class="control-button"
          :disabled="!hasSavedView"
        >
          Load View
        </button>
      </div>
      
      <button
        @click="resetView('Varennes')"
        class="control-button reset-view"
      >
        <font-awesome-icon icon="location-dot" class="location-icon"/>
        Reset View to Varennes
      </button>
      <button
        @click="toggleClipping"
        class="control-button"
        :disabled="!showGoogle3DTiles"
      >
        {{
          isGroundClipped
            ? "Reverse ground clip"
            : "Clip ground"
        }}
      </button>
      <button @click="toggleVarennesGSplats" :class="['control-button', { 'control-button-activated': showVarennesGSplats }]">
        {{
          showVarennesGSplats
            ? "Hide Varennes Gaussian splats"
            : "Show Varennes Gaussian splats"
        }}
      </button>
      <div class="assets-display">
        <button title="Varennes' Trees" class="asset-button" @click="toggleTrees" :class="[{ 'asset-button-activated': !!treeTileset }]">
          <font-awesome-icon icon="tree" class="tree-icon"/>
        </button>
        <button title="Varennes' Fire Hydrants" class="asset-button" @click="toggleFirehydrants" :class="[{ 'asset-button-activated': !!firehydrantTileset }]">
          <font-awesome-icon icon="fire-flame-simple" class="fire-hydrant-icon"/>
        </button>
        <button title="Varennes' Poles" class="asset-button" @click="togglePoles" :class="[{ 'asset-button-activated': !!poleTileset }]">
          <font-awesome-icon icon="tower-cell" class="pole-icon"/>
        </button>
        <button title="Varennes' Buildings" class="asset-button" @click="toggleBuildingAsset" :class="[{ 'asset-button-activated': !!buildingsTileset }]">
          <font-awesome-icon icon="house" class="house-icon"/>
        </button>
      </div>

      <button
        @click="resetView('Office')"
        class="control-button reset-view"
      >
        <font-awesome-icon icon="location-dot" class="location-icon"/>
        Reset View to Office
      </button>
      <button @click="toggleOfficeGSplats" :class="['control-button', { 'control-button-activated': showOfficeGSplats }]">
        {{
          showOfficeGSplats
            ? "Hide Office Gaussian splats #1"
            : "Show Office Gaussian splats #1"
        }}
      </button>
      <button @click="toggleSecondOfficeGSplats" :class="['control-button', { 'control-button-activated': showSecondOfficeGSplats }]">
        {{
          showSecondOfficeGSplats
            ? "Hide Office Gaussian splats #2"
            : "Show Office Gaussian splats #2"
        }}
      </button>

      <button
        @click="resetView('Factory')"
        class="control-button reset-view"
      >
        <font-awesome-icon icon="location-dot" class="location-icon"/>
        Reset View to Factory
      </button>
      <button 
        @click="toggleFactoryGSplats" 
        :class="['control-button', { 'control-button-activated': showFactoryGSplats }]">
        {{
          showFactoryGSplats
            ? "Hide Factory Gaussian splats"
            : "Show Factory Gaussian splats"
        }}
      </button>
      <hr class="separator" />
      <button @click="toggleFlyMode" :class="['control-button', { 'control-button-activated': isFlyMode }]">
        {{ isFlyMode ? "Disable Fly Mode" : "Enable Fly Mode" }}
      </button>
      <button @click="toggleCoordinatePicker" :class="['control-button', { 'control-button-activated': isCoordinatePickerActive }]">
        {{
          isCoordinatePickerActive
            ? "Disable Coordinate Picker"
            : "Enable Coordinate Picker"
        }}
      </button>
      <button
        v-if="!isCurrentlyAbove"
        @click="handleFirstPersonToggle"
        :class="['control-button', 'ground-view-button', { 'ground-view-button-activated' : isFirstPersonMode }]"
      >
        {{
          isFirstPersonMode
            ? "Disable Ground View"
            : "Enable Ground View"
        }}
      </button>
    </div>

    <div v-if="showGoogle3DTiles" class="attribution">
      <p>© Google - Photorealistic 3D Tiles</p>
    </div>

    <div class="debug-group" @click="toggleDebug">
      <button class="asset-button" :class="[isDebugDisplayed ? 'asset-button-activated' : 'asset-button']">
        <font-awesome-icon icon="bug" :class="[isDebugDisplayed ? 'bug-icon-activated' : 'bug-icon']"/>
      </button>

      <div v-if="isDebugDisplayed" class="debug-window">
        <div class="debug-content">
          <span class="debug-label">GPU Memory</span>
          <span class="debug-value">{{ formatMemoryUsage(memoryUsage) }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="isCoordinatePickerActive && lastClickedCoordinate"
      class="coordinate-display"
    >
      <h4>Clicked Coordinate</h4>
      <div class="coordinate-source">
        <strong>Source:</strong>
        <span
          :class="
            lastClickedCoordinate.source === '3dtiles'
              ? 'source-3dtiles'
              : 'source-ellipsoid'
          "
        >
          {{
            lastClickedCoordinate.source === "3dtiles"
              ? "3D Tiles"
              : "Ellipsoid"
          }}
        </span>
      </div>
      <div class="coordinate-info">
        <div>
          <strong>Longitude:</strong>
          {{ lastClickedCoordinate.longitude.toFixed(6) }}°
        </div>
        <div>
          <strong>Latitude:</strong>
          {{ lastClickedCoordinate.latitude.toFixed(6) }}°
        </div>
        <div>
          <strong>Height:</strong>
          {{ lastClickedCoordinate.height.toFixed(2) }}m
        </div>
        <div>
          <strong>Cartesian X:</strong>
          {{ lastClickedCoordinate.cartesian.x.toFixed(2) }}
        </div>
        <div>
          <strong>Cartesian Y:</strong>
          {{ lastClickedCoordinate.cartesian.y.toFixed(2) }}
        </div>
        <div>
          <strong>Cartesian Z:</strong>
          {{ lastClickedCoordinate.cartesian.z.toFixed(2) }}
        </div>
      </div>
    </div>

  </div>
  <div>
    <detail-panel ></detail-panel>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, shallowRef } from "vue";
import * as Cesium from "cesium";
import { storeToRefs } from "pinia";
import useDetailPanelStore from '@/stores/detailPanel.ts'
import { useFlyMode } from "@/composables/useFlyMode";
import { useSettings } from "@/composables/useSettings";
import { useRotationSymbol } from "@/composables/useRotationSymbol";
import { useFirstPersonMode } from "@/composables/useFirstPersonMode";
import { useViewManagement } from "@/composables/useViewManagement";
import { useGoogle3DTiles } from "@/composables/useGoogle3DTiles";
import { useJakartoPointclouds } from "@/composables/useJakartoPointclouds";
import { useCoordinatePicker } from "@/composables/useCoordinatePicker";
import { useMemoryTracking } from "@/composables/useMemoryTracking";
import { useHoverEffect } from '@/composables/useHoverEffect';
import { useClickHandler } from "@/composables/useClickHandler";
import { useAssetTilesets } from "@/composables/useAssetTilesets";
import { useBasemap, type BasemapType } from "@/composables/useBasemap";
import { useGaussianSplats } from "@/composables/useGaussianSplats";
import DetailPanel from '@/components/detail-panel/detail-panel.vue';
import AppHeader from "@/components/AppHeader.vue";
import MeasureToolBar from '@/components/MeasureToolBar.vue'
import type { Sphere } from "@/utils/backendFetch";
import { useGroundClipping } from "@/composables/useGroundClipping";
import { useJakartoWMTS, type WMTSType } from "@/composables/useJakartoWMTS";
import useJakartownsObserver from "@/composables/useJakartownsObserver";

const settings = useSettings();
Cesium.Ion.defaultAccessToken = settings.getString("VITE_CESIUM_ION_TOKEN");
const GOOGLE_API_KEY = settings.getString("VITE_GOOGLE_MAPS_API_KEY");
const GOOGLE_3D_TILES_URL = `https://tile.googleapis.com/v1/3dtiles/root.json?key=${GOOGLE_API_KEY}`;

const treeTilesetPath = '/assets/tree/tileset.json';
const firehydrantTilesetPath = '/assets/firehydrant/tileset.json';
const poleTilesetPath = '/assets/pole/tileset.json';

// Reactive state
const cesiumContainer = ref<HTMLElement>();
const viewer = shallowRef<Cesium.Viewer | null>(null);
const showTerrain = ref<boolean>(true);
const isCurrentlyAbove = ref<boolean>(true);
const selectedBasemap = ref<BasemapType>("none");
const selectedWMTS = ref<WMTSType>("none");
const currentSphere = ref<Sphere | null>(null);
// Track mouse position to report lat/lon under cursor
const lastMousePosition = ref<Cesium.Cartesian2 | null>(null);
const isDebugDisplayed = ref<boolean>(false);
const pointSize = ref<number>(3);

// Stores
const detailPanelStore = useDetailPanelStore()
const { selectedAsset } = storeToRefs(detailPanelStore);

// Minimum camera height above terrain (meters)
const MIN_CAMERA_HEIGHT_ABOVE_TERRAIN_M = ref<number>(40);

// Pre-render listener to enforce minimum camera height
let enforceMinCameraHeight:
  | ((scene: Cesium.Scene, time: Cesium.JulianDate) => void)
  | null = null;
let enforceNoRoll:
  | ((scene: Cesium.Scene, time: Cesium.JulianDate) => void)
  | null = null;

// Track last state to detect crossings below threshold
let wasAboveMinHeight = true;

// Control terrain function
const toggleTerrain = async (initCesium: boolean = false): Promise<void> => {
  if (!viewer.value) return;

  if (!initCesium) {
    showTerrain.value = !showTerrain.value;
  }
  if (showTerrain.value) {
    const terrainProvider = await Cesium.createWorldTerrainAsync();
    viewer.value.terrainProvider = terrainProvider;
  } else {
    viewer.value.terrainProvider = new Cesium.EllipsoidTerrainProvider();
  }
};

const logout = () => {
  window.location.replace('https://solutions.jakarto.com')
}

// INITIALIZE COMPOSABLE
// Basemap
const { changeBasemap } = useBasemap({ viewer, selectedBasemap });

// Jakarto WMTS
const { changeJakartoWMTS } = useJakartoWMTS( viewer, selectedWMTS );

// Google 3D tiles
const { google3DTileset, showGoogle3DTiles, toggleGoogle3DTiles, loadGoogle3DTiles } = useGoogle3DTiles(
  viewer,
  GOOGLE_API_KEY,
  GOOGLE_3D_TILES_URL
);

// Gaussian splats
const { 
  showOfficeGSplats,
  showSecondOfficeGSplats,
  showFactoryGSplats, 
  showVarennesGSplats, 
  toggleOfficeGSplats, 
  toggleSecondOfficeGSplats, 
  toggleFactoryGSplats, 
  toggleVarennesGSplats 
} = useGaussianSplats(viewer);

// Jakarto PC
const { jakartoPointclouds, showJakartoPointclouds, toggleJakartoPointclouds } = useJakartoPointclouds(
  viewer,
  pointSize,
  false // aerial
);
// Aerial PC
const { 
  jakartoPointclouds: aerialPointclouds, 
  showJakartoPointclouds: showAerialPointclouds, 
  toggleJakartoPointclouds: toggleAerialPointclouds 
} = useJakartoPointclouds(
  viewer,
  pointSize,
  true // aerial
);

// Memory usage
const { 
  memoryUsage, 
  startMemoryTracking, 
  stopMemoryTracking, 
  formatMemoryUsage 
} = useMemoryTracking(viewer, google3DTileset, jakartoPointclouds, aerialPointclouds);

const toggleDebug = () => {
  isDebugDisplayed.value = !isDebugDisplayed.value
  viewer.value && (viewer.value.scene.debugShowFramesPerSecond = isDebugDisplayed.value);
}

// Coordinate picker
const { isCoordinatePickerActive, lastClickedCoordinate, toggleCoordinatePicker } = 
  useCoordinatePicker(viewer);

// Rotation symbol
const { setupRotationSymbolHandlers, destroyRotationSymbol } =
  useRotationSymbol(viewer);

// Fly mode
const {
  isFlyMode,
  toggleFlyMode,
  cleanup: cleanupFlyMode,
} = useFlyMode(viewer);

// View management
const {
  hasSavedView,
  saveView,
  loadView,
  resetView,
} = useViewManagement(viewer);

// First person mode
const {
  isFirstPersonMode,
  isFlyingToGround,
  setFirstPersonMode,
  enableFirstPersonMode,
  disableFirstPersonMode,
  handleFirstPersonToggle,
} = useFirstPersonMode(
  viewer,
  currentSphere,
  showJakartoPointclouds,
  toggleJakartoPointclouds,
  showGoogle3DTiles,
  toggleGoogle3DTiles,
);

// Assets 
const { highlightedFeature, currentTileset, initializeHover, resetHighlight } = useHoverEffect(viewer, selectedAsset);

const {  
  clearSelection,
  initializeClick,
  handleAssetClick, 
} = useClickHandler(viewer, {
  onAssetSelected: (asset) => {
    detailPanelStore.setSelectedAsset(asset);
  },
  onPositionUpdate: (coords) => {
    detailPanelStore.setSelectedAssetPosition(coords);
  },
  onPanelOpen: () => {
    detailPanelStore.openPanel();
  },
  onClearSelection: () => {
    detailPanelStore.clearSelectedAsset();
    detailPanelStore.closePanel();
  }
});

const {
  treeTileset,
  firehydrantTileset,
  poleTileset,
  buildingsTileset,
  toggleTrees,
  toggleFirehydrants,
  togglePoles,
  toggleBuildingAsset
} = useAssetTilesets({
  viewer,
  currentTileset,
  resetHighlight,
  clearSelection,         
  treeTilesetPath,
  firehydrantTilesetPath,
  poleTilesetPath,
  selectedAsset
});

// Ground clipping
const { isGroundClipped, toggleClipping, cleanupGroundClipping } = useGroundClipping(viewer, google3DTileset);

// INITIALIZE CESIUM VIEWER
const initCesium = async () => {
  if (!cesiumContainer.value) return;

  try {
    viewer.value = new Cesium.Viewer(cesiumContainer.value, {
      animation: false,
      baseLayerPicker: false,
      fullscreenButton: false,
      vrButton: false,
      geocoder: true,
      homeButton: false,
      infoBox: false,
      sceneModePicker: false,
      selectionIndicator: false,
      timeline: false,
      navigationHelpButton: false,
      navigationInstructionsInitiallyVisible: false,
      scene3DOnly: false,
      shouldAnimate: true,
      useDefaultRenderLoop: true,
      targetFrameRate: 60,
      showRenderLoopErrors: true,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      depthPlaneEllipsoidOffset: -100,
      creditContainer: document.createElement("div"),
    });

    viewer.value.scene.screenSpaceCameraController.inertiaSpin = 0.1;
    viewer.value.scene.screenSpaceCameraController.inertiaTranslate = 0.1;
    viewer.value.scene.screenSpaceCameraController.inertiaZoom = 0.5;
    viewer.value.scene.screenSpaceCameraController.zoomFactor = 12;

    // Improves rendering in some cases, see https://cesium.com/docs/cesiumjs-ref-doc/Viewer.html?#requestRenderMode
    viewer.value.scene.requestRenderMode = true;
    viewer.value.scene.maximumRenderTimeChange = Infinity;
    viewer.value.scene.logarithmicDepthBuffer = false;
    viewer.value.scene.highDynamicRange = true;
    viewer.value.scene.globe.showGroundAtmosphere = false;
    viewer.value.scene.globe.depthTestAgainstTerrain = false;

    selectedBasemap.value = "positron";
    
    changeBasemap();

    resetView('Varennes');

    // Setup rotation symbol handlers
    setupRotationSymbolHandlers();

    // Start memory tracking
    startMemoryTracking();

    toggleTerrain(true);

    // Enforce minimum camera height above terrain
    const scene = viewer.value.scene;
    scene.camera.frustum.near = 0.001;
    const globe = scene.globe;
    const camera = viewer.value.camera;
    let previousPositionWC = camera.positionWC.clone();

    enforceMinCameraHeight = () => {
      if (!viewer.value) return;
      // Avoid unnecessary refresh if camera didn't move
      const distance = Cesium.Cartesian3.distance(
        camera.positionWC,
        previousPositionWC
      );
      if (distance < 0.1) {
        return;
      }
      previousPositionWC = camera.positionWC.clone();

      const carto = Cesium.Cartographic.fromCartesian(camera.positionWC);
      // Terrain height can be undefined if not available yet; treat as 0
      const terrainHeight = globe.getHeight(carto) ?? 0;
      const heightAboveTerrain = carto.height - terrainHeight;
      const minAbove = MIN_CAMERA_HEIGHT_ABOVE_TERRAIN_M.value;
      // Detect threshold crossing due to zoom (before enforcement moves camera)
      isCurrentlyAbove.value = heightAboveTerrain >= minAbove;
      wasAboveMinHeight = isCurrentlyAbove.value;

      // Only enforce minimum height if not in first person mode and not flying to ground
      if (
        !isFirstPersonMode.value &&
        !isFlyingToGround &&
        heightAboveTerrain < minAbove
      ) {
        const delta = minAbove - heightAboveTerrain;
        // Move camera outward along geodetic up (ellipsoid surface normal) by delta meters
        const geodeticUp = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(
          camera.positionWC,
          new Cesium.Cartesian3()
        );
        const move = Cesium.Cartesian3.multiplyByScalar(
          geodeticUp,
          delta,
          new Cesium.Cartesian3()
        );
        camera.position = Cesium.Cartesian3.add(
          camera.position,
          move,
          new Cesium.Cartesian3()
        );
      }
    };
    enforceNoRoll = () => {
      if (!viewer.value) return;
      const camera = viewer.value.camera;
      // optimization: if camera.roll is very close to 0, return
      if (Math.abs(camera.roll) < 1e-6) return;
      camera.setView({
        destination: camera.positionWC,
        orientation: {
          heading: camera.heading,
          pitch: camera.pitch,
          roll: 0.0,
        },
      });
    };
    scene.preRender.addEventListener(enforceMinCameraHeight);
    scene.preRender.addEventListener(enforceNoRoll);

    // Track mouse move to know lon/lat under cursor when threshold events occur
    const handler = new Cesium.ScreenSpaceEventHandler(scene.canvas);
    handler.setInputAction(
      (movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
        lastMousePosition.value = movement.endPosition;
      },
      Cesium.ScreenSpaceEventType.MOUSE_MOVE
    );
    // Store handler for cleanup
    (viewer.value as any)._mouseMoveHandlerForMinHeight = handler;

    // Add escape key listener to exit first person mode
    const escapeKeyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFirstPersonMode.value) {
        disableFirstPersonMode();
      }
    };

    document.addEventListener("keydown", escapeKeyHandler);
    // Store handler for cleanup
    (viewer.value as any)._escapeKeyHandler = escapeKeyHandler;
    console.log("Cesium viewer initialized successfully");
  } catch (error) {
    console.error("Error initializing Cesium viewer:", error);
  }
};

// tileset configuration fetching is handled inside useFirstPersonMode
// Lifecycle hooks
onMounted(() => {
  initCesium();
  initializeHover();
  initializeClick();

  useJakartownsObserver(viewer);
});

onUnmounted(() => {
  // Stop memory tracking
  stopMemoryTracking();

  cleanupGroundClipping();

  if (viewer.value) {
    // Remove preRender listener
    if (enforceMinCameraHeight) {
      viewer.value.scene.preRender.removeEventListener(enforceMinCameraHeight);
      enforceMinCameraHeight = null;
    }
    // Remove mouse move handler
    const mouseHandler = (viewer.value as any)._mouseMoveHandlerForMinHeight;
    if (mouseHandler) {
      mouseHandler.destroy();
      (viewer.value as any)._mouseMoveHandlerForMinHeight = undefined;
    }
    // Remove escape key handler
    const escapeHandler = (viewer.value as any)._escapeKeyHandler;
    if (escapeHandler) {
      document.removeEventListener("keydown", escapeHandler);
      (viewer.value as any)._escapeKeyHandler = undefined;
    }
    // Ensure fly mode is disabled and cleaned up
    cleanupFlyMode();

    // Clean up coordinate picker
    if (isCoordinatePickerActive.value) {
      viewer.value.cesiumWidget.screenSpaceEventHandler.removeInputAction(
        Cesium.ScreenSpaceEventType.LEFT_CLICK
      );
    }
    // Clean up rotation symbol handler
    if ((viewer.value as any).rotationSymbolHandler) {
      (viewer.value as any).rotationSymbolHandler.destroy();
    }
    // Clean up Google 3D Tiles if they exist
    if (google3DTileset.value) {
      viewer.value.scene.primitives.remove(google3DTileset.value);
      google3DTileset.value = null;
    }
    viewer.value.destroy();
    viewer.value = null;
  }
});
</script>

<style scoped>
.cesium-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.cesium-viewer {
  width: 100%;
  height: 100%;
}

.crosshair-cursor {
  cursor: crosshair;
}

.separator {
  border: none;
  border-top: 1px solid var(--secondary);
  box-shadow: 0 1px 6px var(--secondary);
}

.cesium-controls {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 232px;
}

.control-button {
  padding: 6px 10px;
  background-color: rgba(255, 255, 255);
  color: var(--primary);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-family: 'Poppins';
  font-weight: bold;
}

.location-icon {
  padding: 2px 5px 0 0;
}

.reset-view {
  display: flex;
  justify-content: flex-start;
}

.control-button-activated {
  background-color: color-mix(in srgb, var(--primary), transparent 10%);
  color: white;
}

.control-button:hover {
  background-color: color-mix(in srgb, var(--primary), transparent 70%);
  color: white;
}

.control-button:active {
  background-color: var(--primary);
  color: white; 
}

.control-button:disabled {
  background-color: rgba(42, 42, 42, 0.4);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

.control-button:focus {
  outline: none;
}

.ground-view-button {
  border: 2px solid color-mix(in srgb, var(--secondary), transparent 10%);
  color: var(--secondary)
}

.ground-view-button-activated {
  background-color: color-mix(in srgb, var(--secondary) 75%, black 25%);
  color: white;
}

.view-controls {
  display: flex;
  justify-content: space-between;
}

.view-controls button {
  width: 100px;
}

.basemap-select {
  padding: 6px 10px; /* match .control-button */
  background-color: rgba(255, 255, 255);
  color: var(--primary);
  border: none; /* match .control-button */
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px; /* match .control-button */
  width: 100%; /* match .control-button width */
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-family: 'Poppins';
  font-weight: bold;
}

.basemap-select:hover {
  background-color: color-mix(in srgb, var(--primary), transparent 70%);
  color: white;
}

.basemap-select:active {
  background-color: color-mix(in srgb, var(--primary), transparent 60%);
}

.basemap-select:focus {
  outline: none;
}

.attribution {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 11px;
}

.attribution p {
  margin: 0;
  font-weight: 500;
}

.coordinate-display {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.8);
  color:var(--dark-gray);
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 12px;
  max-width: 300px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.coordinate-display h4 {
  margin: 0 0 2px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary);
}

.coordinate-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coordinate-info div {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.coordinate-info strong {
  color: #81c784;
  font-weight: 600;
  min-width: 80px;
}

.coordinate-source {
  margin-bottom: 8px;
  font-size: 11px;
}

.coordinate-source strong {
  color: var(--grey);
  font-weight: 600;
  margin-right: 4px;
}

.source-3dtiles {
  color: var(--secondary);
  font-weight: 600;
}

.source-ellipsoid {
  color: #81c784;
  font-weight: 600;
}

.debug-group {
  position: absolute;
  top: 140px;
  right: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: end;
}

.debug-window {
  margin-top: 86px;
  color: white;
  padding: 8px 12px;
  font-size: 12px;
  min-width: 50px;
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-family: 'Poppins';
}

.debug-content {
  display: flex;
  flex-direction: column;
  text-align: right;
}

.debug-label {
  color: var(--primary);
  font-weight: 600;
  min-width: 50px;
}

.debug-value {
  color: var(--secondary);
  font-weight: 600;
}

.assets-display {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
}

.asset-button {
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.asset-button:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background-color: color-mix(in srgb, var(--primary), transparent 70%);
}

.asset-button-activated {
  background-color: var(--primary);
  border: 1px solid #ffffff50 !important;
}

.tree-icon {
  font-size: 20px;
  color: #45a049;
}

.fire-hydrant-icon {
  font-size: 16px;
  color: #a71212;
}

.house-icon {
  font-size: 20px;
  color: #999999;
}

.pole-icon {
  font-size: 20px;
  color: #8a673e;
}

.bug-icon {
  font-size: 20px;
  color: var(--primary);
}

.bug-icon-activated {
  font-size: 20px;
  color: white;
}

.side-panel {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 25vw;             /* roughly 1/4 of screen */
  min-width: 280px;
  max-width: 420px;
  background: #1f2933;
  color: #f9fafb;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  z-index: 1000;
}

.side-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(15, 23, 42, 0.95);
}

.side-panel__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.side-panel__close {
  border: none;
  background: transparent;
  color: inherit;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}
.side-panel__close:hover {
  opacity: 0.8;
}

.side-panel__content {
  padding: 1rem;
  overflow-y: auto;
  font-size: 0.9rem;
}

/* Slide in/out animation */
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}
.slide-panel-enter-from,
.slide-panel-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.point-size-controls {
  display: flex;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  width: 100%;
}

.point-size-controls label {
  display: flex;
  flex-direction: row;
  padding: 6px 8px;
  font-size: 12px;
  color: var(--primary);
  justify-content: space-around;
  align-items: anchor-center;
}

.point-size-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 50%;
  height: 4px;
  background: var(--primary);
  border-radius: 999px;
  outline: none;
}

.point-size-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  background: var(--primary);
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 0 2px rgba(0,0,0,0.4);
}

.jakarto-wmts {
  position: absolute;
  top: 180px;
  right: 10px;
}
</style>
